import { useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSnapshot, subscribe } from 'valtio';
import { useVisibilityChange } from '@lib/hooks/useVisibilityChange';
import { usePipWindow } from '../hooks/usePipWindow';
import usePhoneState from '@modules/SoftPhoneModule/usePhoneState';
import useToasts from '@modules/ToastModule/useToasts';
import { toastsState } from '@modules/ToastModule';
import { phoneState } from '@modules/SoftPhoneModule/state';
import { pipState, setFloating } from '../state';
import { PIP_MODES, PIP_EVENTS } from '../constants';
import { PipContent } from './PipContent';

export function PipProvider() {
  const { isTabHidden } = useVisibilityChange();
  const { pipWindow, isOpen, open, close } = usePipWindow();
  const { callList, phoneConnected, registrationOverwritten } = usePhoneState();
  const { incomingCallToasts } = useToasts();
  const { enabled, mode, floating } = useSnapshot(pipState);
  const openRef = useRef(open);
  openRef.current = open;

  const hasWebphone = phoneConnected && !registrationOverwritten;

  const hasActiveCall = callList.some((call) =>
    call.direction === 'terminating'
      ? call.hasSession && call.established
      : call.hasSession,
  );
  const hasIncomingCalls = incomingCallToasts.length > 0;
  const hasCallActivity = hasActiveCall || hasIncomingCalls;

  const shouldOpen =
    enabled &&
    hasWebphone &&
    isTabHidden &&
    (mode === PIP_MODES.ALWAYS || hasCallActivity);

  // Listen for "Open Floating Window" from debug panel / settings
  useEffect(() => {
    const handleOpenFloating = async () => {
      await open();
    };
    window.addEventListener(PIP_EVENTS.OPEN_FLOATING, handleOpenFloating);
    return () =>
      window.removeEventListener(PIP_EVENTS.OPEN_FLOATING, handleOpenFloating);
  }, [open]);

  // Register Media Session handler for automatic PiP
  const handleAutoPip = useCallback(async () => {
    if (
      enabled &&
      hasWebphone &&
      (mode === PIP_MODES.ALWAYS || hasCallActivity)
    ) {
      await open();
    }
  }, [enabled, hasWebphone, mode, hasCallActivity, open]);

  useEffect(() => {
    if ('mediaSession' in navigator) {
      try {
        navigator.mediaSession.setActionHandler(
          'enterpictureinpicture' as MediaSessionAction,
          handleAutoPip,
        );
      } catch (error) {
        console.debug('[PiP] Media session handler not supported:', error);
      }

      return () => {
        try {
          navigator.mediaSession.setActionHandler(
            'enterpictureinpicture' as MediaSessionAction,
            null,
          );
        } catch (error) {
          console.debug('[PiP] Media session cleanup error:', error);
        }
      };
    }
  }, [handleAutoPip]);

  // Tell Chrome this page is actively playing media during calls/ringing
  useEffect(() => {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = hasCallActivity
        ? 'playing'
        : 'none';
    }
  }, [hasCallActivity]);

  // Auto open/close based on tab visibility
  useEffect(() => {
    if (pipState.floating || !pipState.enabled) return;

    if (shouldOpen && !isOpen) {
      open();
    } else if (!isTabHidden && isOpen) {
      close();
    }
  }, [shouldOpen, isTabHidden, isOpen, open, close, floating]);

  // Close PiP when webphone disconnects (e.g. another tab takes over)
  // Skip if floating — user explicitly opened via settings preview
  useEffect(() => {
    if (!hasWebphone && isOpen && !floating) {
      close();
    }
  }, [hasWebphone, isOpen, close, floating]);

  // When PiP is closed externally (X button / pagehide), clear floating
  useEffect(() => {
    if (!pipWindow) return;

    const handlePageHide = () => {
      setFloating(false);
    };

    pipWindow.addEventListener('pagehide', handlePageHide);
    return () => pipWindow.removeEventListener('pagehide', handlePageHide);
  }, [pipWindow]);

  // Subscribe to toast state for incoming calls while tab is hidden
  useEffect(() => {
    const unsubscribe = subscribe(toastsState, () => {
      const incoming = toastsState.incomingCallToasts;
      if (
        incoming.length > 0 &&
        document.hidden &&
        pipState.enabled &&
        phoneState.phoneConnected &&
        !phoneState.registrationOverwritten &&
        !window.documentPictureInPicture?.window
      ) {
        openRef.current().catch((error) => {
          console.debug('[PiP] Failed to open for incoming call:', error);
        });

        if (
          typeof Notification !== 'undefined' &&
          Notification.permission === 'granted'
        ) {
          const notification = new Notification('Incoming Call', {
            body: 'You have an incoming call in Contact Center Pro',
            icon: '/favicon.ico',
            requireInteraction: true,
            tag: 'pip-incoming-call',
          });

          notification.onclick = async () => {
            notification.close();
            await openRef.current();
          };

          const timeoutId = setTimeout(() => notification.close(), 30000);
          notification.addEventListener(
            'close',
            () => clearTimeout(timeoutId),
            { once: true },
          );
        }
      }
    });

    if (
      typeof Notification !== 'undefined' &&
      Notification.permission === 'default'
    ) {
      Notification.requestPermission().catch((err) => {
        console.debug('[PiP] Notification permission request failed:', err);
      });
    }

    return unsubscribe;
  }, []);

  if (!isOpen || !pipWindow) {
    return null;
  }

  return createPortal(<PipContent />, pipWindow.document.body);
}
